'use memo';

import { useContext } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  AgencyAgentModelLicenceType,
  useGetAgentsInAgency,
} from '@urbanx/agx.agencies.client';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxSpacer,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx.ui-components2';
import { useFeatureToggles } from 'hooks/useFeatureToggles';

interface ContractFormButtonsProps {
  beginSubmission: () => Promise<void>;
  beginContract: () => Promise<void>;
  beginContractExchange: () => Promise<void>;
  campaignState: CampaignDetailModelState;
  areVendorsValid: boolean;
  disableButtons: boolean;
  isArchived: boolean;
  blankContractIsLive: boolean;
}

const ContractFormButtons = ({
  beginSubmission,
  beginContract,
  beginContractExchange,
  campaignState,
  areVendorsValid,
  disableButtons,
  isArchived,
  blankContractIsLive,
}: ContractFormButtonsProps) => {
  const screenSize = useWindowSize();
  const { userAccount } = useContext(AuthContext);

  const { enableNswContractExchange } = useFeatureToggles(campaignState);
  const { data: agencyAgents, isPending: agencyAgentsPending } =
    useGetAgentsInAgency(
      campaignState === CampaignDetailModelState.NSW && enableNswContractExchange
    );
  const currentAgent = agencyAgents?.find(
    agent => agent.id === userAccount.agent
  );

  const hasNswClassOneOrTwoLicence =
    currentAgent?.licenceType === AgencyAgentModelLicenceType.Class1 ||
    currentAgent?.licenceType === AgencyAgentModelLicenceType.Class2;
    
  const contractExchangeLicenceTooltip =
    !agencyAgentsPending && !hasNswClassOneOrTwoLicence
      ? 'Only team members with a NSW Class 1 or Class 2 licence can exchange contracts.'
      : undefined;

  switch (campaignState) {
    case CampaignDetailModelState.NSW:
      return (
        <AgxSpacer
          direction="vertical"
          gap="xs"
          contentAlignment="center"
          fill
          className="campaignModalButtons"
          padding={
            screenSize === Breakpoints.Mobile ||
            screenSize === Breakpoints.Tablet_Portrait
              ? undefined
              : { left: 'spacing-7xl', right: 'spacing-7xl' }
          }
        >
          <AgxButton
            id="agx-begin-contract"
            dataTestId="agx-begin-contract"
            variant="primary"
            size="large"
            stretch
            text={enableNswContractExchange ? 'Sales Advice' : 'New Contract'}
            onClick={beginContract}
            disabled={!areVendorsValid || disableButtons || isArchived}
          />
          {enableNswContractExchange && (
            <AgxButton
              id="agx-contract-exchange"
              variant="hollow"
              size="large"
              stretch
              text="Contract Exchange"
              onClick={beginContractExchange}
              disabled={
                !blankContractIsLive ||
                !areVendorsValid ||
                disableButtons ||
                isArchived ||
                !hasNswClassOneOrTwoLicence
              }
              tooltip={contractExchangeLicenceTooltip}
            />
          )}
          <AgxButton
            id="agx-submit-contract"
            dataTestId="agx-submit-contract"
            variant="hollow"
            size="large"
            stretch
            text="Submit Contract"
            onClick={beginSubmission}
            disabled={!areVendorsValid || disableButtons || isArchived}
          />
        </AgxSpacer>
      );
    case CampaignDetailModelState.QLD:
    default:
      return (
        <AgxSpacer
          direction="vertical"
          gap="xs"
          contentAlignment="center"
          fill
          className="campaignModalButtons"
          padding={
            screenSize === Breakpoints.Mobile ||
            screenSize === Breakpoints.Tablet_Portrait
              ? undefined
              : { left: 'spacing-7xl', right: 'spacing-7xl' }
          }
        >
          <AgxButton
            id="agx-submit-contract"
            dataTestId="agx-submit-contract"
            variant="hollow"
            size="large"
            stretch
            text="Submit Contract"
            onClick={beginSubmission}
            disabled={disableButtons || isArchived}
          />
          <AgxButton
            id="agx-begin-contract"
            dataTestId="agx-begin-contract"
            variant="primary"
            size="large"
            stretch
            text="New Contract"
            onClick={beginContract}
            disabled={!blankContractIsLive || disableButtons || isArchived}
          />
        </AgxSpacer>
      );
  }
};

export default ContractFormButtons;
